import { z } from 'zod'
import { RISER_MAX_UNITS } from '../helpers/riser.js'
import { primitiveSchema } from './primitive.dto.js'
import { textAnnotationFields } from './text-annotation.dto.js'

/**
 * A movable object on a floor plan. This discriminated union is the single
 * source of truth for object shapes — both the API (persists it as JSON,
 * validated on the way in) and the web editor derive their `FloorObject` type
 * from it, so the client and the stored data cannot drift.
 *
 * All furniture is one `furniture` variant: its shape is a free-form list of
 * `primitives` (designed in the furniture editor and copied onto the placement),
 * not a fixed per-kind geometry. `label` and `note` are annotations, not
 * furniture, so they stay their own variants.
 */
const baseObject = {
  id: z.string(),
  x: z.number(),
  y: z.number(),
  rotation: z.number(),
  /** Objects sharing a groupId move/rotate as one unit. */
  groupId: z.string().optional(),
  /**
   * In an event plan, the venue inventory item this placement was drawn from.
   * Absent = the client brought it (doesn't draw down venue inventory).
   */
  sourceItemId: z.string().optional(),
}

export const floorObjectSchema = z.discriminatedUnion('type', [
  // A piece of furniture: rendered from its `primitives`. `label` is an optional
  // per-instance caption (e.g. "Table 5") drawn centered on the piece.
  z.object({
    ...baseObject,
    type: z.literal('furniture'),
    primitives: z.array(primitiveSchema),
    /** A caption the planner typed for this piece. Drawn on the plan. */
    label: z.string().optional(),
    /**
     * The table's number on the seating chart, as the source system assigned it.
     *
     * Not a caption and not drawn: Prismm does not print table numbers across its
     * tables, and one drawn inside a piece's own rotation reads upside down as soon
     * as the table is turned. It is kept because it identifies the table — the
     * seating chart, the place cards and the caterer's sheet all use it.
     */
    tableNumber: z.string().optional(),
    /**
     * How many units of its item this piece is, for an item that comes in
     * units: a riser runway is several risers end to end. Absent means one.
     * It is what the piece draws down from stock — see `stockUnits`.
     *
     * Capped here as well as in the editor, so a save that did not come from the
     * editor cannot claim a warehouse of stock and leave other events short.
     */
    units: z.number().int().positive().max(RISER_MAX_UNITS).optional(),
  }),
  // A text annotation drawn on the plan. Its point is the top-left corner of the
  // first line, which is where the canvas puts it — not the middle of the text.
  z.object({
    ...baseObject,
    ...textAnnotationFields,
    type: z.literal('label'),
  }),
  // A pinned annotation: a marker on the plan whose `text` is revealed on hover
  // and edited inline. Used to annotate event plans.
  z.object({ ...baseObject, type: z.literal('note'), text: z.string() }),
])

export type FloorObject = z.infer<typeof floorObjectSchema>
